// src/utils/parseColoredText.js
// ─────────────────────────────────────────────────────────────────────────────
// Parses a tagged string like:
//   "[yellow]हळदीच्या लागवडीत वाढ,[/yellow] [white]बाजारभावांचा...[/white]"
// into an array of { text, color } objects.
//
// Supported tags:
//   [yellow]  → #FFD700
//   [white]   → #FFFFFF
//   [green]   → #A8E6A3
//   [orange]  → #FF8C00
//   plain text (no tag) → inherits parent color
// ─────────────────────────────────────────────────────────────────────────────

export const COLOR_MAP = {
  yellow: "#FFD700",
  white:  "#FFFFFF",
  green:  "#A8E6A3",
  orange: "#FF8C00",
  red:    "#FF6B6B",
};

/**
 * Parse a tagged string into segments.
 * @param {string} text - Tagged string e.g. "[yellow]word[/yellow] rest"
 * @returns {Array<{ text: string, color: string|null }>}
 *   color is null for untagged plain text (inherits from CSS parent)
 */
export function parseColoredText(text) {
  if (!text) return [];

  const segments = [];
  // Regex matches either a tagged block OR plain text between tags
  const regex = /\[(\w+)\]([\s\S]*?)\[\/\1\]|([^\[]+)/g;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match[1] && match[2] !== undefined) {
      // Tagged segment: [color]text[/color]
      segments.push({
        text:  match[2],
        color: COLOR_MAP[match[1]] || null,
        tag:   match[1],
      });
    } else if (match[3]) {
      // Plain untagged text
      segments.push({
        text:  match[3],
        color: null,
        tag:   null,
      });
    }
  }

  return segments;
}

/**
 * Strip all color tags from a tagged string, returning plain text.
 * Useful for meta descriptions, SEO, search indexing.
 * @param {string} text - Tagged string
 * @returns {string} Plain text
 */
export function stripColorTags(text) {
  if (!text) return "";
  return text.replace(/\[\/?\w+\]/g, "").trim();
}

/**
 * Check whether a string contains any color tags.
 * @param {string} text
 * @returns {boolean}
 */
export function hasColorTags(text) {
  if (!text) return false;
  return /\[\w+\]/.test(text);
}
